import {
	BreadcrumbCompat as Breadcrumb,
	IBreadcrumbItem,
	PivotCompat as Pivot,
	PivotItem,
} from 'components/FluentUICompat';

import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {ProjectDetailsPageParams} from '../vehicleProjectPagePaths';
import {useCommand} from 'hooks';
import {UserRole, VehicleProject} from 'types';
import {
	AssignVehicleProjectRequirementsForm,
	PermissionsForm,
	ProjectRelevanceButton,
} from '../components';
import {DetailsField, DetailsSection} from 'components';

import VehicleProjectDetailsTabRequirements from '../AssignedEntities/VehicleProjectDetailsTabRequirements';
import VehicleProjectDetailsTabRegulations from '../AssignedEntities/VehicleProjectDetailsTabRegulations';
import VehicleProjectDetailsTabParagraphs from '../AssignedEntities/VehicleProjectDetailsTabParagraphs';
import {
	GetVehicleProjectDetailsQuery,
	useGetVehicleProjectDetailsQuery,
	useRemoveAssignedVehicleProjectRequirementsMutation,
} from '../hooks';
import {formatDateTime} from 'i18n/localeDateFormat';
import {i18n} from 'i18next';
import {
	EntityContextProvider,
	useEntityContext,
} from 'components/EntityPage/EntityContext';
import {ConfirmDeleteDialog, useDialogState} from 'components/Dialogs';
import {AssignedRequirementWithID} from '../AssignedEntities/AssignedRequirementColumns';
import {AggregateVehicleProjectDialog} from '../components/AggregateButtons';
import {
	BaselineCmdsInfo,
	useBaselineCmds,
} from 'features/Baselines/utils/baselineCmds';
import {BaselineCreateDialog} from 'features/Baselines/components/BaselineCreateDialog';
import {
	getBodyWorkVariants,
	getDriveVariants,
	getEarliestPosEtVsi,
	getProjectName,
	getTechnologyVariants,
} from '../components/vehicleProjectColumns';

export const VehicleProjectDetailsPage: React.FC = () => {
	const {t} = useTranslation('features/vehicleprojects');

	const [currentViewKey, setCurrentViewKey] = useState<string | undefined>(
		'AssignedRequirements',
	);

	const {modelSeries, generation} = useParams() as ProjectDetailsPageParams;

	const {data: dataVehPro} = useGetVehicleProjectDetailsQuery({
		variables: {
			generation: generation ?? '',
			modelSeries: modelSeries ?? '',
		},
	});

	const onLinkClick = (item?: any) => {
		setCurrentViewKey(item?.props.accessKey);
	};
	return (
		<>
			<VehicleProjectBreadCrumb />
			<VehicleProjectsAssignRequirements
				modelSeries={modelSeries}
				generation={generation}
			/>
			<VehicleProjectsDetailsPagePermission />
			<VehicleProjectsDetailsPageBaseLine />
			<AggregateVehicleProjectDialog />
			<VehicleProjectDetailsSection dataVehPro={dataVehPro} />
			<Pivot
				aria-label='Views'
				style={{paddingLeft: 20}}
				onLinkClick={onLinkClick}
			>
				<PivotItem
					headerText={t('AssignedRequirements')}
					accessKey='AssignedRequirements'
					itemIcon={
						currentViewKey === 'AssignedRequirements' ? 'List' : undefined
					}
				></PivotItem>
				{dataVehPro && (
					<PivotItem
						headerText={t('AssignedRegulations')}
						accessKey='AssignedRegulations'
						itemIcon={
							currentViewKey === 'AssignedRegulations' ? 'List' : undefined
						}
					></PivotItem>
				)}
				{dataVehPro && (
					<PivotItem
						headerText={t('AssignedParagraphs')}
						accessKey='AssignedParagraphs'
						itemIcon={
							currentViewKey === 'AssignedParagraphs' ? 'List' : undefined
						}
					></PivotItem>
				)}
			</Pivot>
			<EntityContextProvider>
				<VehicleProjectDetailsTabRequirements
					dataVehPro={dataVehPro}
					hidden={currentViewKey !== 'AssignedRequirements'}
				/>
				<RemoveAssignedRequirement />
				{currentViewKey === 'AssignedRequirements' && (
					<ProjectRelevanceButton />
				)}
			</EntityContextProvider>
			<EntityContextProvider>
				<VehicleProjectDetailsTabRegulations
					dataVehPro={dataVehPro}
					hidden={currentViewKey !== 'AssignedRegulations'}
				/>
				{currentViewKey === 'AssignedRegulations' && <ProjectRelevanceButton />}
			</EntityContextProvider>
			<VehicleProjectDetailsTabParagraphs
				dataVehPro={dataVehPro}
				hidden={currentViewKey !== 'AssignedParagraphs'}
			/>
		</>
	);
};

const VehicleProjectBreadCrumb: React.FC = () => {
	const {t} = useTranslation('features/vehicleprojects');

	const navigate = useNavigate();

	const {modelSeries, generation} = useParams() as ProjectDetailsPageParams;

	const itemsWithHref: IBreadcrumbItem[] = [
		{
			text: t('VehicleProjects'),
			key: 'vehicleProjects',
			onClick: () => navigate('/vehicleProjects'),
		},
		{
			text: `${modelSeries}`,
			key: 'modelSeries',
		},
		{
			text: `${generation}`,
			key: 'generation',
		},
	];

	return <Breadcrumb items={itemsWithHref} />;
};

type VehicleProjectDetailsSectionProps = {
	dataVehPro: GetVehicleProjectDetailsQuery | undefined;
};
export const VehicleProjectDetailsSection: React.FC<
	VehicleProjectDetailsSectionProps
> = ({dataVehPro}) => {
	const {t, i18n} = useTranslation('features/vehicleprojects');

	const {generation} = useParams() as ProjectDetailsPageParams;

	const initialFieldRows: DetailsField[][] = React.useMemo(() => {
		const vehicleProjects =
			(dataVehPro?.vehicleProjectsByModelSeriesAndGeneration ??
				[]) as VehicleProject[];
		const bodyworkVariants = getBodyWorkVariants(vehicleProjects);
		const driveVariants = getDriveVariants(vehicleProjects);
		const earliestPosEtVsi = getEarliestPosEtVsi(vehicleProjects);
		const technologyVariants = getTechnologyVariants(vehicleProjects);

		return [
			[
				{
					data: dataVehPro?.vehicleProjectMasterByModelSeriesAndGeneration?.derivates.filter(
						x => x.trim() !== '',
					),
					type: 'stringarray',
					label: t('Derivative'),
				},
				{
					data: dataVehPro?.vehicleProjectMasterByModelSeriesAndGeneration
						?.sopVsi,
					type: 'date',
					label: t('EarliestSOP'),
				},
				{
					data: dataVehPro?.vehicleProjectMasterByModelSeriesAndGeneration
						?.eopVsi,
					type: 'date',
					label: t('LatestEOP'),
				},
				{
					data: dataVehPro?.vehicleProjectMasterByModelSeriesAndGeneration
						?.lastEvaluated,
					type: 'date',
					label: t('LastEvaluated'),
				},
			],
			[
				{
					data: bodyworkVariants,
					type: 'stringarray',
					label: t('BodyworkVariants'),
				},
				{
					data: driveVariants,
					type: 'stringarray',
					label: t('DriveVariants'),
				},
				{
					data: earliestPosEtVsi,
					type: 'date',
					label: t('EarliestPOS'),
				},
				{
					data: technologyVariants,
					type: 'stringarray',
					label: t('Technology'),
				},
			],
			[
				{
					data: dataVehPro?.vehicleProjectsByModelSeriesAndGeneration.map(x =>
						getProjectName(x as VehicleProject, i18n),
					),
					type: 'stringarray',
					label: t('VehicleProjects'),
				},
			],
		];
	}, [dataVehPro, t, i18n]);

	return (
		<DetailsSection title={generation} initialFieldRows={initialFieldRows} />
	);
};

interface VehicleProjectsAssignRequirementsProps {
	modelSeries: string;
	generation: string;
}

const VehicleProjectsAssignRequirements: React.FC<
	VehicleProjectsAssignRequirementsProps
> = props => {
	const navigate = useNavigate();
	// Use the refetch function somewhere in your component
	const refreshData = async () => {
		navigate(0);
	};

	return (
		<AssignVehicleProjectRequirementsForm
			refreshData={refreshData}
			{...props}
		/>
	);
};

const VehicleProjectsDetailsPagePermission: React.FC = () => {
	return <PermissionsForm />;
};

const VehicleProjectsDetailsPageBaseLine: React.FC = () => {
	const params = useParams() as ProjectDetailsPageParams;

	const {infoForIsSuccessDialogOpen}: BaselineCmdsInfo = useBaselineCmds({
		params,
		showOverviewCmd: true,
	});

	return (
		<BaselineCreateDialog
			generation={params.generation}
			modelSeries={params.modelSeries}
			infoForIsOpen={infoForIsSuccessDialogOpen}
		/>
	);
};

const RemoveAssignedRequirement: React.FC = () => {
	const {t} = useTranslation('features/vehicleprojects');

	const {modelSeries, generation} = useParams() as ProjectDetailsPageParams;
	const navigate = useNavigate();
	const {selectedItems} = useEntityContext<AssignedRequirementWithID>();
	const [deleteDialogState, showDeleteDialog, hideDeleteInfinityDialog] =
		useDialogState();

	const [removeAssignedRequirements] =
		useRemoveAssignedVehicleProjectRequirementsMutation();

	const handleRemoveClick = React.useCallback(async () => {
		await removeAssignedRequirements({
			variables: {
				input: {
					modelSeries,
					generation,
					requirementIds: selectedItems
						.filter(x => x.addedAutomatically === false)
						.map(x => x.id),
				},
			},
		});
		navigate(0);
		hideDeleteInfinityDialog();
	}, [hideDeleteInfinityDialog, selectedItems]);

	useCommand(
		{
			key: 'remove',
			text: t('RemoveRequirements'),
			iconProps: {iconName: 'Remove'},
			priority: 3,
			disabled: !selectedItems.some(x => x.addedAutomatically === false),
			onClick() {
				showDeleteDialog();
			},
			roles: [UserRole.SystemAdministrator, UserRole.Bedarfskoordinator],
		},
		[selectedItems],
	);

	return (
		<ConfirmDeleteDialog {...deleteDialogState} onConfirm={handleRemoveClick} />
	);
};

export default VehicleProjectDetailsPage;
